import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isLoggingOut = false;
    private readonly publicSessionBypassUrls = [
        '/auth.php',
        '/login_admin.php',
        '/login_otp.php',
        '/login_catalogue.php',
        '/get_admin_session.php',
        '/log.php',
        '/otp.php',
        '/verify_otp.php',
        '/reg_otp.php',
        '/verify_regotp.php',
        '/get_icard.php',
        '/get_setting.php',
        '/get_regsetting.php',
        '/get_ticket_category.php',
        '/get_ticket_category_by_category.php',
        '/get_event_by_id.php',
        '/uploadimage.php',
        '/add_registration.php',
        '/generate_card.php',
        '/attendance.php',
        '/display.php',
        '/add_registration_with_regfee.php',
        '/get_coupon_code_by_code.php',
        '/check_coupon.php',
        '/get_registration_by_id_web.php',
        '/get_payment_setting.php',
        '/send_bluewaba1.php',
        '/edit_transaction_for_free_entry.php',
        '/create_order.php',
        '/get_payment_details.php',
    ];

    constructor(
        private auth: AuthService,
        private router: Router
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Only intercept requests destined for admin_services
        if (!req.url.includes('/admin_services/')) {
            return next.handle(req);
        }

        // Allow public login and OTP validation endpoints to bypass session check
        if (this.isPublicBypassUrl(req.url)) {
            return next.handle(req);
        }

        // If user is not logged in or token was cleared in storage, immediately block API call
        if (!this.auth.isLoggedIn()) {
            this.handleLogout('Please log in to continue.');
            return throwError(() => new HttpErrorResponse({
                status: 401,
                statusText: 'Unauthorized',
                url: req.url,
                error: { flag: false, msg: 'User is not logged in or credentials missing' }
            }));
        }

        // Validate session token with backend (auth.php)
        return this.auth.validateSession().pipe(
            switchMap((res: any) => {
                // If backend session check failed or returned flag: false
                if (!res || res.flag !== true) {
                    const errorMsg = res?.msg || 'This account was logged in on another device. Please login again.';
                    this.handleLogout(errorMsg);
                    return throwError(() => new HttpErrorResponse({
                        status: 401,
                        statusText: 'Unauthorized',
                        url: req.url,
                        error: { flag: false, msg: errorMsg }
                    }));
                }

                const token = this.auth.getToken() || '';
                const userId = this.auth.getUserId() || '';

                // Attach token and credentials to the outgoing request
                const authorizedReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`,
                        'X-Session-Token': token,
                        'X-User-ID': `${userId}`
                    },
                    withCredentials: true
                });

                return this.forwardRequest(authorizedReq, next);
            }),
            catchError((error: any) => {
                this.handleAuthError(error);
                return throwError(() => error);
            })
        );
    }

    private handleLogout(message?: string) {
        if (this.isLoggingOut) {
            return;
        }

        this.isLoggingOut = true;
        this.auth.logout();
        this.router.navigate(['/admin-auth'], {
            replaceUrl: true,
            queryParams: {
                reason: message || 'This account was logged in on another device. Please login again.',
            },
        }).finally(() => {
            this.isLoggingOut = false;
        });
    }

    private isPublicBypassUrl(url: string): boolean {
        return this.publicSessionBypassUrls.some((bypassUrl) => url.includes(bypassUrl));
    }

    private forwardRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                this.handleAuthError(error);
                return throwError(() => error);
            })
        );
    }

    private handleAuthError(error: any) {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            const msg = error.error?.msg || (typeof error.error === 'string' ? error.error : '') || 'This account was logged in on another device. Please login again.';
            this.handleLogout(msg);
        }
    }
}
